import { useState } from "react";
import { format } from "date-fns";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useCreateCalendarEvent } from "@/hooks/useCalendar";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { formatError } from "@/lib/utils";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultDate?: Date;
  defaultStartTime?: string; // "HH:mm"
};

export function CreateEventDialog({
  open,
  onOpenChange,
  defaultDate,
  defaultStartTime,
}: Props) {
  const createEvent = useCreateCalendarEvent();
  const queryClient = useQueryClient();

  const [title, setTitle] = useState("");
  const [date, setDate] = useState(
    defaultDate ? format(defaultDate, "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd")
  );
  const [startTime, setStartTime] = useState(defaultStartTime || "09:00");
  const [endTime, setEndTime] = useState(() => {
    if (defaultStartTime) {
      const [h, m] = defaultStartTime.split(":").map(Number);
      return `${String(h + 1).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    }
    return "10:00";
  });
  const [description, setDescription] = useState("");
  const [attendees, setAttendees] = useState("");

  // Reset when dialog opens with new defaults
  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      setTitle("");
      setDate(defaultDate ? format(defaultDate, "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd"));
      setStartTime(defaultStartTime || "09:00");
      if (defaultStartTime) {
        const [h, m] = defaultStartTime.split(":").map(Number);
        setEndTime(`${String(h + 1).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
      } else {
        setEndTime("10:00");
      }
      setDescription("");
      setAttendees("");
    }
    onOpenChange(isOpen);
  };

  const handleCreate = () => {
    if (!title.trim()) {
      toast.error("Title is required");
      return;
    }

    const startIso = `${date}T${startTime}:00`;
    const endIso = `${date}T${endTime}:00`;

    const attendeeList = attendees
      .split(",")
      .map((e) => e.trim())
      .filter(Boolean);

    createEvent.mutate(
      {
        title: title.trim(),
        start_time: startIso,
        end_time: endIso,
        description: description || undefined,
        attendees: attendeeList.length > 0 ? attendeeList : undefined,
      },
      {
        onSuccess: () => {
          toast("Event created");
          queryClient.invalidateQueries({ queryKey: ["calendar-events"] });
          queryClient.invalidateQueries({ queryKey: ["calendar-events-range"] });
          handleOpenChange(false);
        },
        onError: (err) => toast.error(formatError(err)),
      }
    );
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent className="sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>New Event</SheetTitle>
          <SheetDescription className="sr-only">
            Create a new Google Calendar event
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 mt-6">
          <div className="space-y-2">
            <Label>Title</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Event title"
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label>Date</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Start</Label>
              <Input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>End</Label>
              <Input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional details..."
              rows={3}
              className="text-sm"
            />
          </div>

          <div className="space-y-2">
            <Label>Attendees</Label>
            <Input
              value={attendees}
              onChange={(e) => setAttendees(e.target.value)}
              placeholder="email@example.com, another@example.com"
            />
            <p className="text-xs text-muted-foreground">
              Comma-separated email addresses
            </p>
          </div>

          <Button
            onClick={handleCreate}
            className="w-full"
            disabled={createEvent.isPending}
          >
            {createEvent.isPending ? "Creating..." : "Create Event"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
